// @ts-strict-ignore
import type { CustomerTestTypes } from '@usealloy/typegen';
import get from 'lodash/get';
import {
  buildURLParamsString,
  buildUrlParamsStringWithCompressedQuery,
} from './api';

const wrapVanityDomainOnRoute = (route, orgSlugForVanityDomain) => {
  const url = new URL(window.location.href);
  const domainSplit = url.host.split('.');
  const rootDomain = domainSplit.slice(1).join('.');
  let updatedRoute = route;
  if (domainSplit.length > 3) { // implicit assumption that a vanity domains are only application on <prefix>.app.<suffix>
    updatedRoute = `${url.protocol}//${orgSlugForVanityDomain}.${rootDomain}${route}`;
  }
  return updatedRoute;
};

/**
 * We have multiple routes to track different services.
 * Be careful about editing as this may have cascading effects.
 */

/**
 * @description Daffy Back End Routes
 */
const routes = {
  beta: {
    cookie: '/beta/cookie/',
    apiLogs: {
      list: '/beta/api-logs',
    },
    journeys: {
      applications: {
        list: '/beta/journeys/applications',
      },
    },
  },
  authentication: {
    mfa: {
      verifyNumber: (uid, token) => `/mfa/verify/${uid}/${token}/`,
      verifyCodeTotp: (uid, token) => `/mfa/verify/${uid}/${token}/`,
      verifyCode: (uid, token) => `/mfa/${uid}/${token}/`,
      createVerification: '/mfa/verifications/',
      cancelVerification: sid => `/mfa/verifications/${sid}/cancel/`,
      checkVerification: '/mfa/verifications/check/',
    },
    changePassword: '/password_change/',
    resetPassword: {
      basic: '/v2/password_reset/',
      withToken: token => `/v2/password_reset/${token}/`,
    },
    login: {
      basic: '/v2/login/',
      googleOauth: '/v2/login/oauth/google/',
      jwtOauth: '/v2/login/oauth/jwt/',
      login_method: '/v2/login_method/',
    },
    logout: next => `/logout/${next && `?next=${next}`}`,
    agentInvitation: token => `/v2/invitation/${token}/`,
    verifyEmail: token => `/v2/verify_email/${token}/`,
    verifyCustomerEmail: token => `/v2/verify_customer_email/${token}/`,
    state: '/auth/state/',
  },
  admin: {
    agents: {
      invite: '/admin/agents/invite/',
    },
    customers: {
      data: '/admin/customers/api',
      applications: customerId => `/admin/customers/${customerId}/applications/`,
      agents: customerId => `/admin/customers/${customerId}/agents`,
      services: customerId => `/admin/customers/${customerId}/services/`,
      detailData: customerId => `/admin/customers/${customerId}/data/`,
      switchCustomerContext: customerId => `/admin/customers/${customerId}/session/`,
      ruleLibrarySession: '/admin/customers/rule-library/session/',
      ruleLibrarySessionWithNext: nextUrl => `/admin/customers/rule-library/session/?next_url=${encodeURIComponent(nextUrl)}`,
    },
    employees: {
      list: '/admin/employees/',
      listData: '/admin/employees/data/',
      migrate: employeeId => `/admin/employees/${employeeId}/migrate/`,
    },
    evaluations: {
      list: '/admin/evaluations/',
      data: '/admin/evaluations/data/',
    },
    notifications: {
      list: '/admin/notifications/',
      data: '/admin/notifications/data/',
    },
    reindexTokens: '/admin/reindex-tokens/',
    services: {
      list: '/admin/services/',
      data: '/admin/services/data/',
      detail: serviceId => `/admin/services/${serviceId}/`,
      downloadBulkAttributesTemplate: serviceId => `/admin/services/${serviceId}/attributes/bulk_attributes_template_download`,
      validateBulkAttributes: serviceId => `/admin/services/${serviceId}/attributes/bulk_attributes/validate`,
      uploadBulkAttributes: serviceId => `/admin/services/${serviceId}/attributes/bulk`,
    },
    workflows: {
      clone: '/admin/applications/clone/',
    },

  },
  applications: {
    list: '/applications/',
    listData: '/applications/data/',
    afefListData: '/applications/afef/data/',
    detail: appToken => `/applications/${appToken}/`,
    detailData: appToken => `/applications/${appToken}/data/`,
    championChallenger: appToken => `/applications/${appToken}/champion-challenger/`,
    params: '/applications/params/',
    versions: {
      create: appToken => `/applications/${appToken}/versions/create/`,
      data: (appToken: string, version?: number | string) => `/applications/${appToken}/versions/data/${version ? `?public_version=${version}` : ''}`,
      props: appToken => `/applications/${appToken}/versions/props/`,
      update: (appToken, version) => `/applications/${appToken}/versions/${version}/update/`,
    },
    workflowTestHistory: '/test-history/workflow-test-history',
    whatIfAnalysisData: analysisToken => `/test-history/what-if-analysis-data?analysis_token=${analysisToken}`,
    whatIf: appToken => `/applications/${appToken}/what-if/`,
    whatIfAnalysis: appToken => `/applications/${appToken}/what-if/analysis/`,
    whatIfEvaluation: appToken => `/applications/${appToken}/what-if/evaluation/`,
    whatIfSampleSize: appToken => `/applications/${appToken}/what-if/sample-size/`,
    whatIfWorkflowVersions: appToken => `/applications/${appToken}/what-if/workflow-versions/`,
  },
  approvals: {
    data: '/approvals/data',
  },
  audit: {
    data: '/audit/data',
    auditActions: '/agent-audit-actions/audit_actions',
  },
  deforestation: '/deforestation/',
  documentUpload: '/documents/upload/',
  entities: {
    list: '/entities/',
    props: '/entities/props/',
    data: '/entities/data/',
    pii: entityToken => `/entities/${entityToken}/pii`,
    personPii: entityToken => `/entities/persons/${entityToken}/`,
    businessPii: entityToken => `/entities/businesses/${entityToken}/`,
    detail: entityToken => `/entities/${entityToken}`,
    detailData: entityToken => `/entities/${entityToken}/data/`,
    evaluation: {
      create: ({ entityToken }) => `/entities/${entityToken}/evaluations/`,
      detail: ({ entityToken, evaluationToken }) => `/entities/${entityToken}/evaluations/${evaluationToken}/?json=true`,
      data: ({ entityToken, evaluationToken }) => `/entities/${entityToken}/evaluations/${evaluationToken}/data/`,
    },
    review: (entityToken, workflowToken) => `/entities/${entityToken}/applications/${workflowToken}/review/`,
    reviewData: ({ entityToken, reviewToken }) => `/entities/${entityToken}/reviews/${reviewToken}/data/`,
  },
  env: '/env/',
  evaluations: {
    list: '/evaluations/',
    listData: '/evaluations/data/',
    props: '/evaluations/props/',
    run: '/evaluations/run/',
    runData: '/evaluations/run/data/',
    detail: ({ evaluationToken, orgSlugForVanityDomain }: {
      evaluationToken: string;
      orgSlugForVanityDomain?: string;
    }) => {
      const route = `/evaluations/${evaluationToken}/`;
      if (orgSlugForVanityDomain) {
        return wrapVanityDomainOnRoute(route, orgSlugForVanityDomain);
      }
      return route;
    },
    workflow: ({ route, orgSlugForVanityDomain }: {
      route: string;
      orgSlugForVanityDomain?: string;
    }) => {
      if (orgSlugForVanityDomain) {
        return wrapVanityDomainOnRoute(route, orgSlugForVanityDomain);
      }
      return route;
    },
    piiIndicators: evaluationToken => `/evaluations/${evaluationToken}/pii-indicators/`,
    metadataTemplates: '/evaluations/metadata_templates/',
    serviceResponses: {
      detail: (evaluationToken, serviceResponseId) => `/evaluations/${evaluationToken}/service-responses/${serviceResponseId}`,
    },
    fraudScores: evaluationToken => `/evaluations/${evaluationToken}/fraud-scores/`,
    dataSources: {
      statuses: evaluationToken => `/evaluations/${evaluationToken}/data-sources-statuses`,
      vendorAPICalls: (evaluationToken, actionId) => `/evaluations/${evaluationToken}/vendor-api-calls/${actionId}`,
    },
  },
  journey_applications: {
    search: '/journey_applications/search',
  },
  events: '/events/',
  groups: {
    detail: entityToken => `/groups/${entityToken}/`,
    review: entityToken => `/groups/${entityToken}/review/`,
  },
  serviceFraudScores: '/service_fraud_scores',
  login: '/login/',
  logout: next => `/logout/${next && `?next=${next}`}`,
  notes: '/notes/',
  queues: {
    list: '/queues/',
    data: '/queues/data/',
    next: '/queues/next/',
    reasonCodeOptions: '/queues/props/reason-code-options',
  },
  sso: {
    customerMetadata: customerId => `/sso/metadata/customer/${customerId}`,
    validateMetadata: '/sso/metadata/customer/validate',
  },
  sendEmail: '/send_email/',
  sessionData: '/session/data/',
  sessionPing: '/session_security/ping/',
  settings: {
    list: '/settings/',
    lists: {
      entities: '/settings/lists/entities/',
    },
    customLists: {
      version: (customListToken, version) => `/settings/custom-lists/${customListToken}/versions/${version}/`,
    },
    reasonCodeGroups: '/settings/reason-code-groups/',
    reasons: '/settings/reasons/',
    queries: {
      data: '/settings/queries/',
    },
    roles: {
      list: '/settings/roles/',
      permissions: '/settings/roles/permissions/',
      tabQueries: '/settings/roles/queries/tabs/',
      export: '/settings/roles/export',
      import: '/settings/roles/import/',
    },
    samples: {
      fingerprint: '/settings/samples/fingerprint/',
      capture: '/settings/samples/capture/',
    },
    services: '/settings/services/',
    tags: '/settings/tags/',
    webhooks: {
      list: '/settings/webhooks/',
      test: '/settings/webhooks/test/',
    },
    whitelistIpAddresses: '/settings/whitelist-ip-addresses/',
  },
  switchCustomer: customerId => `/switch_customer?customer_id=${customerId}`,
  transactions: {
    detail: '/transactions/',
    accountInfo: '/transactions/account-info',
    accountHistory: '/transactions/account-history',
  },
  investigations: {
    queue: '/investigations/',
    csv: '/investigations/csv',
    queueFilters: '/investigations/filters',
    detail: (investigationToken: string, organizationNameSlug?: string) => {
      const route = `/investigations/${investigationToken}`;
      if (organizationNameSlug) {
        return wrapVanityDomainOnRoute(route, organizationNameSlug);
      }
      return route;
    },
    promote: investigationToken => `/investigations/${investigationToken}/promote`,
    review: investigationToken => `/investigations/${investigationToken}/review`,
    sarDraft: ({ investigationToken }) => `/investigations/${investigationToken}/report/new`,
    sarReport: ({ investigationToken, sarReportToken, sarReportVersion = undefined }) => `/investigations/${investigationToken}/report/${sarReportToken}${sarReportVersion ? `/${sarReportVersion}` : ''}`,
    accountsAndTransactions: investigationToken => `/investigations/${investigationToken}/accounts-and-transactions`,
  },
  sars: {
    list: '/sars',
  },
  v3: {
    admin: {
      agents: {
        list: '/v3/admin/agents/',
        customerAccess: '/v3/admin/agents/customer-access/',
      },
      logs_decrypt: '/v3/admin/logs_decrypt/',
      logs_decrypt_old: '/v3/admin/logs_decrypt_old/',
      metrics: '/v3/admin/metrics/',
      roles: '/v3/admin/roles/',
      customers: '/v3/admin/customers/',
      aws_auth_v4: '/v3/admin/auth/aws-sig-v4/',
      checkAccess: '/v3/admin/customers/access-check/',
      jwt_auth: (jwt_type: string) => `/v3/admin/auth/jwt/${jwt_type}/`,
      provisioning_access_info: '/v3/admin/customers/provisioning/access-info/',
      provisioning_afef_access_info: '/v3/admin/customers/provisioning/afef-access-info/',
      provisioning_additional_vanity_domain: '/v3/admin/customers/provisioning/additional-vanity-domain',
      global_services: {
        list: '/v3/admin/global-services/',
        attribute_active_workflows: (attributeToken: string) => `/v3/dashboard/admin/global-services/attributes/${attributeToken}/workflows/`,
        detail: (internalName: string) => `/v3/admin/global-services/${internalName}`,
      },
      afef_child_customers: '/v3/admin/customers/afef/' as const,
      afef_customer_access: '/v3/admin/customers/afef-customer-access/',
    },
    auth: {
      aws_auth_v4: '/v3/auth/aws-sig-v4/',
    },
    auditActions: '/v3/audit_actions/',
    cases: {
      filterOptions: '/v3/cases/filter-options/',
      alertColors: '/v3/cases/alert-colors',
      assignees: '/v3/cases/assignees/',
    },
    afef: {
      childCustomers: '/v3/customers/afef/child-customers/',
    },
    dashboard: {
      authentication: {
        login: '/v3/dashboard/login/',
        'reset-password': token => `/v3/dashboard/reset-password/${token}`,
        'mfa-totp-verify-code': token => `/v3/dashboard/mfa/totp/${token}`,
        'forgot-password': '/v3/dashboard/forgot-password',
        'mfa-setup-verify-phonenumber': token => `/v3/dashboard/mfa/verify/${token}`,
        'mfa-verify-code': token => `/v3/dashboard/mfa/${token}`,
        'mfa-setup-totp': token => `/v3/dashboard/mfa/totp/verify/${token}`,
      },
      admin: {
        ai: {
          view: '/v3/dashboard/admin/ai/',
        },
        customers: {
          list: '/v3/dashboard/admin/customers/',
        },
        metrics: {
          view: '/v3/dashboard/admin/metrics/',
        },
        employees: {
          list: '/v3/dashboard/admin/employees/',
        },
        evaluations: {
          list: '/v3/dashboard/admin/evaluations/',
        },
        notifications: {
          list: '/v3/dashboard/admin/notifications/',
        },
        services: {
          list: '/v3/dashboard/admin/services/',
        },
        globalServices: {
          list: '/v3/dashboard/admin/global-services/',
          detail: serviceInternalName => `/v3/dashboard/admin/global-services/${serviceInternalName}`,
          attribute: {
            create: serviceInternalName => `/v3/dashboard/admin/global-services/${serviceInternalName}/attribute/create/`,
            update: serviceInternalName => `/v3/dashboard/admin/global-services/${serviceInternalName}/attribute/update/`,
            duplicate: serviceInternalName => `/v3/dashboard/admin/global-services/${serviceInternalName}/attribute/duplicate/`,
            detail: (serviceInternalName, attrToken) => `/v3/dashboard/admin/global-services/${serviceInternalName}/attribute/${attrToken}/`,
          },
        },
        customerAccess: {
          list: '/v3/dashboard/admin/customer-access/',
        },
      },
      audit: {
        list: '/v3/dashboard/audit',
      },
      cases: {
        detail: caseToken => `/v3/dashboard/cases/${caseToken}`,
        list: '/v3/dashboard/cases/',
        // used for "Saved Searches" which will break if the SPA specific part of the route is included
        saved: '/cases/',
        sarDraft: caseToken => `/v3/dashboard/cases/${caseToken}/report/new`,
        sarReport: ({ caseToken, sarReportToken, sarReportVersion = undefined }) => `/v3/dashboard/cases/${caseToken}/report/${sarReportToken}${sarReportVersion ? `/${sarReportVersion}` : ''}`,
      },
      ctrs: {
        list: '/v3/dashboard/ctrs',
        new: '/v3/dashboard/ctrs/new',
        details: (reportToken: string) => `/v3/dashboard/ctrs/${reportToken}` as const,
      },
      entities: {
        list: '/v3/dashboard/entities/',
        portfolioEvaluation: ({ entityToken, portfolioEvaluationToken }) => `/v3/dashboard/entities/${entityToken}/portfolio-evaluations/${portfolioEvaluationToken}`,
      },
      evaluations: {
        list: '/v3/dashboard/evaluations/',
      },
      journeys: {
        applications: {
          detail: ({ journeyApplicationToken, journeyToken, orgSlugForVanityDomain }: {
            journeyApplicationToken: string;
            journeyToken: string;
            orgSlugForVanityDomain?: string;
          }) => {
            const route = `/v3/dashboard/journeys/${journeyToken}/applications/${journeyApplicationToken}`;
            if (orgSlugForVanityDomain) {
              return wrapVanityDomainOnRoute(route, orgSlugForVanityDomain);
            }
            return route;
          },
          filterOptions: '/v3/workflows/filter-options/',
          list: '/v3/dashboard/journeys/applications/',
        },
        alerts: {
          detail: ({ journeyApplicationToken, journeyToken, orgSlugForVanityDomain }: {
            journeyApplicationToken: string;
            journeyToken: string;
            orgSlugForVanityDomain?: string;
          }) => {
            const route = `/v3/dashboard/journeys/${journeyToken}/alerts/${journeyApplicationToken}`;
            if (orgSlugForVanityDomain) {
              return wrapVanityDomainOnRoute(route, orgSlugForVanityDomain);
            }
            return route;
          },
          filterOptions: '/v3/workflows/alerts/filter-options/',
          list: '/v3/dashboard/journeys/alerts/',
        },
        list: '/v3/dashboard/journeys/',
        shadowTest: {
          results: ({
            journeyToken,
            shadowTestToken,
          }) => `/v3/dashboard/journeys/${journeyToken}/shadow-test/${shadowTestToken}`,
        },
        multiEnvironmentConfig: {
          detail: ({
            journeyToken,
            multiEnvironmentConfigToken,
          }) => `/v3/dashboard/journeys/${journeyToken}/multi-environment-config/${multiEnvironmentConfigToken}`,
        },
        testing: ({ journeyToken, versionId }) => `/v3/dashboard/journeys/${journeyToken}/versions/${versionId}/testing`,
        versions: {
          list: journeyToken => `/v3/dashboard/journeys/${journeyToken}/versions/`,
          detail: ({ journeyToken, versionNum }) => `/v3/dashboard/journeys/${journeyToken}/versions/${versionNum}`,
        },
        actions: '/v3/dashboard/journeys/actions/',
      },
      home: {
        dashboard: '/v3/dashboard/home',
      },
      queues: {
        list: '/v3/dashboard/queues/',
      },
      notifications: {
        list: '/v3/dashboard/notifications',
      },
      portfolioEvaluations: {
        list: '/v3/dashboard/portfolio-evaluations',
        portfolioEvaluation: (
          portfolioEvaluationToken: string,
          filters?: Record<string, string[] | string>,
        ) => {
          let qp = '';
          if (filters) {
            qp = buildURLParamsString({ q: filters }, true);
          }
          return `/v3/dashboard/portfolio-evaluations/${portfolioEvaluationToken}${qp}`;
        },
      },
      reports: {
        detail: ({ bsaId }) => `/v3/dashboard/reports/${bsaId}`,
      },
      settings: {
        index: '/v3/dashboard/settings/',
        agents: '/v3/dashboard/settings/agents/',
        roles: '/v3/dashboard/settings/roles/',
        reason_codes: '/v3/dashboard/settings/reason-codes/',
        case_management: '/v3/dashboard/settings/case-management/',
        entity_feedback: '/v3/dashboard/settings/entity-feedback/',
        review_reasons: '/v3/dashboard/settings/review-reasons/',
        sandbox_values: '/v3/dashboard/settings/sandbox-values/',
        webhooks: '/v3/dashboard/settings/webhooks/',
        services: '/v3/dashboard/settings/services/',
        denylist: '/v3/dashboard/settings/denylist/',
        allowlist: '/v3/dashboard/settings/allowlist/',
        lists: '/v3/dashboard/settings/lists/',
        list_entities: listToken => `/v3/dashboard/settings/listEntities/${listToken}`,
        multi_level_attribute_filters: '/v3/dashboard/settings/multi-level-attribute-filters/',
        outcomes: '/v3/dashboard/settings/outcomes/',
        tags: '/v3/dashboard/settings/tags/',
        events: '/v3/dashboard/settings/events/',
        webhook_logs: '/v3/dashboard/settings/webhook-logs', // TODO: update to /webhooks/logs once webhook can be moved to SPA for proper routing
        searches: '/v3/dashboard/settings/searches/',
        published_attributes: '/v3/dashboard/settings/published-attributes',
        custom_lists: '/v3/dashboard/settings/custom-lists/',
        custom_list_versions: ({ customListToken, showWorkflows = false }) => `/v3/dashboard/settings/custom-lists/${customListToken}/${showWorkflows ? '?showWorkflows=true' : ''}`,
        custom_list_version: (customListToken, version) => `/v3/dashboard/settings/custom-lists/${customListToken}/versions/${version}`,
      },
      workflows: {
        list: '/v3/dashboard/workflows/',
        details: workflowToken => `/v3/dashboard/workflows/${workflowToken}/`,
        versions: {
          create: workflowToken => `/v3/dashboard/workflows/${workflowToken}/versions/create`,
          update: (workflowToken, publicVersion) => `/v3/dashboard/workflows/${workflowToken}/versions/${publicVersion}/update`,
        },
        // Curried so we can use partial application
        whatIfEval: (workflowToken: string, analysisToken: string) => (evalToken: string) => `
          /v3/dashboard/workflows/${workflowToken}/what-if/${analysisToken}/evaluations/${evalToken}
        `,
        completedWhatIfResults: (workflowToken: string, analysisToken: string) => `/v3/dashboard/workflows/${workflowToken}/what-if/${analysisToken}/completed-results`,
        policyTestResults: (workflowToken: string, testType: CustomerTestTypes, testToken: string) => `/v3/dashboard/workflows/${workflowToken}/${testType}/${testToken}`,
      },
      partnerSolutions: {
        list: '/v3/dashboard/partner-solutions/',
        detail: internalName => `/v3/dashboard/partner-solutions/${internalName}`,
        attribute: {
          create: serviceInternalName => `/v3/dashboard/partner-solutions/${serviceInternalName}/attributes/create`,
          update: (serviceInternalName, attributeId) => `/v3/dashboard/partner-solutions/${serviceInternalName}/attributes/${attributeId}/update`,
          duplicate: (serviceInternalName, attributeId) => `/v3/dashboard/partner-solutions/${serviceInternalName}/attributes/${attributeId}/duplicate`,
          detail: (serviceInternalName, attributeId) => `/v3/dashboard/partner-solutions/${serviceInternalName}/attributes/${attributeId}`,
        },
      },
      aiOverview: {
        list: '/v3/dashboard/ai-assistant/',
        // Single parameterized route — matches the ":agentKey" param in dashboard/index.tsx's
        // route registration, so the URL slug is always the agent's own key.
        agentDetail: (agentKey: string) => `/v3/dashboard/ai-assistant/agents/${agentKey}`,
      },
      aiTesting: {
        landing: '/v3/dashboard/ai-testing/',
        new: '/v3/dashboard/ai-testing/new',
        results: ({ workflowToken, testToken }: { workflowToken: string; testToken: string }) => (
          `/v3/dashboard/ai-testing/${workflowToken}/${testToken}`
        ),
      },
    },
    multiLevelAttributes: '/v3/multi-level-attributes',
    notifications: '/v3/notifications/',
    settings: {
      account: '/v3/settings/account/data/',
      apiKeys: '/v3/dashboard/settings/apiKeys/',
      list: '/v3/dashboard/settings/',
      multiLevelAttributeFilters: '/v3/settings/multi-level-attribute-filters/',
      permissionActions: '/v3/settings/permission-actions/',
      tagTypes: '/v3/settings/case-management/tag-types/',
      agents: {
        list: '/v3/settings/agents/',
        data: '/v3/settings/agents/data/',
        role: '/v3/settings/agents/role/',
        export: '/v3/settings/agents/export/',
        permissions: {
          data: '/v3/settings/agents/permissions/data/',
        },
        update: agentId => `/v3/settings/agents/${agentId}/`,
      },
      organization: {
        data: '/v3/settings/organization/data/',
      },
      tags: {
        categories: '/v3/settings/tags/categories/',
      },
      publishedAttributes: '/v3/dashboard/settings/published-attributes/',
      feedPayloads: '/v3/settings/feed-payloads/',
      authSettings: {
        ssoExceptions: '/v3/settings/auth-settings/sso-exceptions/',
        samlMetadata: '/v3/settings/auth-settings/saml/metadata/',
        saml: '/v3/settings/auth-settings/saml/',
        otherConfig: '/v3/settings/auth-settings/other-config/',
        samlCertificate: '/v3/settings/auth-settings/saml/certificate/',
      },
    },
    userContext: '/v3/user_context/',
    workflows: {
      list: '/v3/dashboard/workflows/',
      webhook: '/v3/workflows/webhooks/',
    },
    services: internalName => `/v3/services/${internalName}`,
  },
  workflows: {
    versions: {
      update: (appToken, publicVersion) => (
        `/applications/${appToken}/versions/${publicVersion}/update/`
      ),
    },
  },
};

/**
 * @description Typhon Back End Routes
 */
const typhonRoutes = {
  agentManagement: {
    agents: {
      status: (agentId: string) => `/agent-management/agents/${agentId}/status`,
    },
  },
  fraudSignal: {
    config: '/fraud-signal/config',
    activateModel: '/fraud-signal/activate-model',
    modelHistory: '/fraud-signal/model-history',
  },
  ai: {
    promptTemplates: {
      get: '/ai/prompts',
      archive: (featureCategory: string) => `/ai/prompts/${featureCategory}`,
    },
    promptTemplateVersions: {
      get: (featureCategory: string, isSystemPrompt?: boolean) => `/ai/prompts/${featureCategory}${isSystemPrompt ? buildURLParamsString({ isSystemPrompt }, true) : ''}`,
      create: (isSystemPrompt?: boolean) => `/ai/prompts${isSystemPrompt ? buildURLParamsString({ isSystemPrompt }, true) : ''}`,
      activate: (featureCategory: string, publicVersion: number, isSystemPrompt?: boolean) => `/ai/prompts/${featureCategory}/${publicVersion}${isSystemPrompt ? buildURLParamsString({ isSystemPrompt }, true) : ''}`,
    },
    customPromptContexts: {
      get: '/ai/custom-prompt-context',
      upsert: '/ai/custom-prompt-context',
      activate: (publicVersion: number) => `/ai/custom-prompt-context/versions/${publicVersion}`,
      archive: '/ai/custom-prompt-context/archive',
      unarchive: '/ai/custom-prompt-context/unarchive',
    },
    feedback: {
      // The AI service resolves the resource token to its most recently mapped LLM session, so
      // any of L-/S-/JA-/C-/INV-/EA-/P-/B-/D- works. We send the journeyApplicationToken.
      create: (resourceToken: string) => `/ai/feedback/${resourceToken}`,
    },
  },
  // Not under /ai — this is a native Typhon route, not proxied to the external AI service.
  journeyAiAgentConfigs: {
    get: '/journey-ai-agent-configs',
    create: '/journey-ai-agent-configs',
    delete: (journeyToken: string, journeyNodePublicId: string) => `/journey-ai-agent-configs/${journeyToken}/nodes/${journeyNodePublicId}`,
  },
  aiOverview: {
    interest: '/ai-assistant/interest',
    usage: '/ai-assistant/usage',
    backtestHistory: '/ai-assistant/backtest-history',
    backtestJourneys: '/ai-assistant/backtest-journeys',
  },
  applications: {
    whatIf: {
      analysis: (analysisToken: string) => `/what-if/${analysisToken}`,
    },
  },
  cloning: {
    schema: '/cloning/schema',
    clone: '/cloning/clone',
  },
  entities: {
    credit: ({ entityToken, evaluationToken }) => `/entities/${entityToken}/evaluations/${evaluationToken}/credit/`,
    llmSummary: ({ entityToken, evaluationToken }) => `/entities/${entityToken}/evaluations/${evaluationToken}/summary/`,
    customerListEntitiesColumns: '/customer-list-entities-columns/',
    documents: {
      get: entityToken => `/entities/${entityToken}/documents`,
      single: ({ documentToken, entityToken }) => `/entities/${entityToken}/documents/${documentToken}/`,
    },
    pii: entityToken => `/entities/${entityToken}/pii`,
    entityFraudSnapshot: entityToken => `/entityFraudSnapshot/${entityToken}`,
    entityFraudSnapshotByEvaluation: evaluationToken => `/entityFraudSnapshot/byEvaluation/${evaluationToken}`,
    entityFraudSnapshotHistory: entityToken => `/entityFraudSnapshot/${entityToken}/history`,
    publishedAttributes: entityToken => `/entities/${entityToken}/published-attributes`,
    publishedAttributesData: entityToken => `/entities/${entityToken}/published-attributes-data`,
    transactions: {
      descriptions: '/transactions/descriptions/',
      getAll: entityToken => `/entities/${entityToken}/transactions`,
      getSingle: (entityToken, transactionId) => `/entities/${entityToken}/transactions?external_transaction_id=${transactionId}`,
    },
    getAssociatedJourneyApplications: entityToken => `/journeys/entities/${entityToken}/applications`,
    timeline: entityToken => `/entities/${entityToken}/timeline`,
    pdfExport: entityToken => `/entities/${entityToken}/pdf-export`,
    actionThresholdResults: ({ entityToken, evaluationToken, queryParams }) => `/entities/${entityToken}/evaluations/${evaluationToken}/threshold-results${buildURLParamsString(queryParams, true)}`,
    actionEvidences: ({
      entityToken,
      evaluationToken,
      actionId,
      queryParams,
    }) => `/entities/${entityToken}/evaluations/${evaluationToken}/threshold-results/${actionId}/evidence${buildURLParamsString(queryParams, true)}`,
    thresholdEvidenceExportCsv: ({
      entityToken,
      evaluationToken,
      actionId,
      thresholdId,
      queryParams,
    }) => `/entities/${entityToken}/evaluations/${evaluationToken}/threshold-results/${actionId}/evidence/${thresholdId}/export/csv${buildURLParamsString(queryParams, true)}`,
    getEntityByNameTokenOrId: ({
      entityName, entityToken, externalEntityId,
    }) => {
      const queryParams = `${entityToken ? `entityToken=${entityToken}` : ''}${entityName ? `${entityToken ? '&' : ''}fullName=${entityName}` : ''}${externalEntityId ? `${entityToken || entityName ? '&' : ''}externalEntityId=${externalEntityId}` : ''}`;
      return `/entities/get-entity-by-name-token-or-id?${queryParams}`;
    },
    getEntityGroupByNameTokenOrId: ({
      entityToken, externalEntityId,
    }) => {
      const token = entityToken || externalEntityId;
      return `/entity-groups/${token}`;
    },
    getEntityPortfolioEvaluations: entityToken => `/entities/${entityToken}/portfolio-evaluations/shortlist`,
  },
  entityFeedback: {
    get: '/entity-feedback',
    append: '/entity-feedback',
    getOptions: '/entity-feedback-options',
  },
  events: {
    get: eventRequestToken => `/v1/events/${eventRequestToken}`,
  },
  evidence: {
    upload: '/evidence',
    fetch: ({ contextToken, sourceTokens }: { contextToken?: string; sourceTokens?: string[] }) => {
      const params = new URLSearchParams();
      if (contextToken) {
        params.append('context_token', contextToken);
      }
      if (sourceTokens && sourceTokens.length > 0) {
        params.append('source_tokens', JSON.stringify(sourceTokens));
      }
      return `/evidence?${params.toString()}`;
    },
  },
  notes: {
    createNote: '/notes',
  },
  customerSettings: {
    reasonCodes: '/reason-codes',
  },
  cases: {
    alerts: ({ caseToken, evaluationToken, portfolioEvaluationToken }, queryParams) => {
      // NOTE: we need to handle encoding special characters. 2nd param is
      // false to handle apostrophes and quotation marks (as well as other special characters)
      const url = `/cases/${caseToken}/evidences/${evaluationToken || portfolioEvaluationToken}/alerts${buildURLParamsString(queryParams, false)}`;
      return url;
    },
    list: '/cases',
    detail: caseToken => `/cases/${caseToken}`,
    evidence: caseToken => `/cases/${caseToken}/evidences`,
    work: caseToken => `/cases/${caseToken}/works`,
    search: (searchParameters, compressedQuery = null) => {
      const url = compressedQuery
        ? `/cases${buildUrlParamsStringWithCompressedQuery(searchParameters, compressedQuery, true)}`
        : `/cases${buildURLParamsString(searchParameters, true)}`;
      return url;
    },
    promotedAlerts: (caseToken: string) => `/cases/${caseToken}/promoted-alerts`,
  },
  fraudAttackRadar: {
    alerts: {
      getAll: '/fraud-radar/alerts',
      get: (farAlertToken: string) => `/fraud-radar/alerts/${farAlertToken}`,
      update: (farAlertToken: string) => `/fraud-radar/alerts/${farAlertToken}`,
    },
    analytics: {
      get: (farConfigToken: string) => `/fraud-radar/data/${farConfigToken}`,
    },
    configurations: {
      get: '/fraud-radar/configs',
      create: '/fraud-radar/configs',
      update: (farConfigToken: string) => `/fraud-radar/configs/${farConfigToken}`,
    },
  },
  ruleNotificationPolicies: {
    create: '/rule-notification-policies',
    get: '/rule-notification-policies',
  },
  sars: {
    prefill: entityToken => `/sar/prefill/${entityToken}`,
    newReport: '/sar/report/',
    newReportType: (reportToken, queryParams) => `/sar/report/${reportToken}${buildURLParamsString(queryParams, true)}`,
    report: (reportToken, version?) => `/sar/report/${reportToken}${buildURLParamsString(version, true)}`,
    reportByCase: caseToken => `/sar/report/case/${caseToken}`,
    reportByInvestigation: investigationToken => `/sar/report/investigation/${investigationToken}`,
    reports: options => `/sar/reports${buildURLParamsString(options, true)}`,
    work: reportToken => `/sar/report/${reportToken}/works`,
    note: (reportToken: string) => `/sar/report/${reportToken}/notes` as const,
    assignableAgents: '/sar/agents/assignable',
  },
  secrets: {
    alloyToCustomer: {
      createGpgKey: '/secrets/alloy-to-customer/gpg',
      createSshKey: '/secrets/alloy-to-customer/ssh',
      getPublicKeys: '/secrets/alloy-to-customer',
    },
    customerToAlloy: {
      createGpgKey: '/secrets/customer-to-alloy/gpg',
      createSshKey: '/secrets/customer-to-alloy/ssh',
      getPublicKeys: '/secrets/customer-to-alloy',
    },
  },
  seedData: '/seed-data/sandbox-persona/journeys',
  seedDataDate: '/seed-data/date',
  // NOTE: Ensure we hit the private router "/transactions-dashboard" for Daffy specific use-cases.
  //        The public router is for different use cases.
  trx: {
    patch: {
      status: '/transactions-dashboard/status',
    },
  },
  // NOTE: Ensure we hit the private router "/transactions-dashboard" for Daffy specific use-cases.
  //        The public router is for different use cases.
  bankAccounts: {
    patch: {
      status: '/bank-accounts-dashboard/status',
    },
  },
  caseManagementAnalytics: {
    caseStatuses: '/case-management-analytics/case-status',
    dailyCasesCreated: '/case-management-analytics/daily-cases-created',
    tagsClosedCaseReasons: '/case-management-analytics/tag-closed-case-reasons',
    tagsTriggeredTimeSeries: '/case-management-analytics/tags-triggered-time-series',
    tagsTriggered: '/case-management-analytics/tags-triggered',
    caseClosedReasonsTimeSeries: '/case-management-analytics/case-closed-reasons',
  },
  documents: {
    documentTypes: {
      get: '/document-types',
    },
  },
  documentUpload: '/documents',
  embeddedFinance: {
    cloneJourney: ({ journeyToken }) => `/embedded-finance/journeys/${journeyToken}/clone` as const,
    getAllJourneyFederationStatus: () => '/embedded-finance/journeys/federation-status' as const,
    getJourneyFederationStatus: ({ journeyToken }) => `/embedded-finance/journeys/${journeyToken}/federation-status` as const,
    getWorkflowFederationStatus: ({ workflowToken }) => `/embedded-finance/workflows/${workflowToken}/federation-status` as const,
  },
  matrixModels: {
    create: '/matrix-models',
    update: matrixModelToken => `/matrix-models/${matrixModelToken}/`,
    get: (matrixModelToken, matrixModelVersion = '') => `/matrix-models/${matrixModelToken}/${matrixModelVersion}`,
  },
  journeyAnalytics: {
    journeysExist: '/journey-analytics/journeys-exist',
    journeyApplicationDataSources: '/journey-analytics/data-sources',
    journeyOutcomes: '/journey-analytics/outcomes',
    openJourneyApplicationStatuses: '/journey-analytics/open-statuses',
    manualReviewOutcomes: '/journey-analytics/manual-review-outcomes',
    journeyOutcomesTimeSeries: '/journey-analytics/outcomes-time-series',
    journeyApplicationTotals: '/journey-analytics/total-applications',
    journeyApplicationTotalsTimeSeries: '/journey-analytics/total-applications-time-series',
    journeyApplicationTotalReviews: '/journey-analytics/total-reviews',
    journeyStepOutcomeDetails: '/journey-analytics/journey-step-outcomes',
    workflowAttributes: {
      reasonCodes: (workflowToken: string) => (
        `/journey-analytics/workflow-attributes/${workflowToken}/reason-codes`
      ),
      reviewReasons: (workflowToken: string) => (
        `/journey-analytics/workflow-attributes/${workflowToken}/review-reasons`
      ),
      tagsTriggered: (workflowToken: string) => (
        `/journey-analytics/workflow-attributes/${workflowToken}/tags-triggered`
      ),
      outcomesOverTime: (workflowToken: string) => (
        `/journey-analytics/workflow-attributes/${workflowToken}/outcomes-over-time`
      ),
      totalEvalsOverTime: (workflowToken: string) => (
        `/journey-analytics/workflow-attributes/${workflowToken}/total-evals-over-time`
      ),
      reviewedEvalOutcomes: (workflowToken: string) => (
        `/journey-analytics/workflow-attributes/${workflowToken}/reviewed-eval-outcomes`
      ),
    },
  },
  workforceAnalytics: {
    onboarding: {
      onboardingSummary: '/workforce-analytics/onboarding-summary',
      onboardingAgentPerformance: '/workforce-analytics/onboarding-agent-performance',
      onboardingTopKpis: '/workforce-analytics/onboarding-top-kpis',
    },
    ongoing: {
      ongoingSummary: '/workforce-analytics/ongoing-summary',
      ongoingAgentPerformance: '/workforce-analytics/ongoing-agent-performance',
    },
  },
  journeyTests: {
    getHistory: journeyToken => `/journey-tests/${journeyToken}/history`,
    sandboxPersonas: {
      runTest: (journeyToken: string, publicVersion: number) => (
        `/journey-tests/${journeyToken}/version/${publicVersion}/sandbox-personas`
      ),
      getProgress: (testToken: string) => (
        `/journey-tests/sandbox-personas/${testToken}/status`
      ),
    },
    shadowTest: {
      create: '/journey-tests/shadow-test',
      get: (shadowTestToken: string) => `/journey-tests/shadow-test/${shadowTestToken}`,
      getActiveTest: (journeyToken: string) => `/journey-tests/shadow-test?journey_token=${journeyToken}`,
      end: (shadowTestToken: string) => `/journey-tests/shadow-test/${shadowTestToken}/end`,
    },
    multiEnvironmentConfig: {
      get: (multiEnvironmentConfigToken: string) => `/journey-tests/multi-environment-config/${multiEnvironmentConfigToken}`,
      create: '/journey-tests/multi-environment-config',
    },
  },
  journeys: {
    archive: journeyToken => `/journeys/${journeyToken}/archive`,
    batches: {
      create: journeyToken => `/journeys/${journeyToken}/batches`,
      getBatchApplications: (journeyToken, journeyBatchToken) => `/journeys/${journeyToken}/batches/${journeyBatchToken}/applications`,
      getBatch: (journeyToken, journeyBatchToken) => `/journeys/${journeyToken}/batches/${journeyBatchToken}`,
      getVersionBatches: (journeyToken, versionId) => `/journeys/${journeyToken}/versions/${versionId}/batches`,
    },
    create: '/journeys/',
    createJourney: journeyToken => `/v1/journeys/${journeyToken}/applications`,
    updateJourneyApplication: ({
      journeyToken,
      journeyApplicationToken,
    }: {
      journeyToken: string;
      journeyApplicationToken: string;
    }) => `/journeys/${journeyToken}/applications/${journeyApplicationToken}/`,
    get: journeyToken => `/journeys/${journeyToken}`,
    getBranchNames: (journeyToken: string) => `/journeys/${journeyToken}/branch-names`,
    getJourneysForPermissions: '/journeys/journeys-for-permissions',
    getJourneysWithPagination: '/journeys/journeys-with-pagination',
    getJourneyVersionServices: ({
      journeyToken,
      queryParams,
    }: {
      journeyToken: string;
      queryParams: string;
    }) => `/journeys/${journeyToken}/versions/services?${queryParams}`,
    getJourneyVersionActions: ({
      journeyToken,
      publicVersion,
    }: {
      journeyToken: string;
      publicVersion: string;
    }) => `/journeys/${journeyToken}/versions/${publicVersion}/actions`,
    getSchema: journeyToken => `/v1/journeys/${journeyToken}/schema`,
    getSamplePayload: (journeyToken, branchName, version) => `/journeys/${journeyToken}/schema/${branchName}/sample-payload?version=${version}`,
    validatePayload: (journeyToken, branchName) => `/journeys/${journeyToken}/schema/${branchName}/validate-payload`,
    getVersions: journeyToken => `/journeys/${journeyToken}/versions`,
    settings: journeyToken => `/journeys/${journeyToken}/settings`,
    getJourneysByWorkflow: workflowToken => `/journeys/workflows/${workflowToken}`,
    getJourneysWithWorkflows:
      queryParameters => `/journeys/workflows${buildURLParamsString(queryParameters, true)}`,
    createVersion: journeyToken => `/journeys/${journeyToken}/versions`,
    checkJourneyNameForConflict:
      queryParameters => `/journeys/check-journey-name-for-conflict${buildURLParamsString(queryParameters)}`,
    checkWorkflowNamesForConflicts: '/journeys/checkWorkflowNamesForConflicts',
    cloneVersion: (journeyToken, versionId) => `/journeys/${journeyToken}/versions/${versionId}/clone`,
    getVersion: (journeyToken, versionId) => `/journeys/${journeyToken}/versions/${versionId}`,
    archiveVersion: (journeyToken, versionId) => `/journeys/${journeyToken}/versions/${versionId}/archive`,
    applications: {
      archiveApplication: ({ journeyApplicationToken, journeyToken }) => `/journeys/${journeyToken}/applications/${journeyApplicationToken}/archive`,
      escalate: ({ journeyApplicationToken, journeyToken }) => `/journeys/${journeyToken}/applications/${journeyApplicationToken}/escalate`,
      getApplication: ({ journeyApplicationToken, journeyToken }) => `/journeys/${journeyToken}/applications/${journeyApplicationToken}`,
      getJourneyApplicationAssignableAgents: ({ checkAssignPermissions }) => `/journeys/applications/assignableAgents?checkAssignPermissions=${checkAssignPermissions}`,
      getJourneyApplicationAssignableAgentsWithPermissions:
        queryParameters => `/journeys/applications/assignable-agents-with-permissions${buildURLParamsString(queryParameters, true)}`,
      getJourneyApplicationAssignedAgents: ({ journeyApplicationToken, journeyToken }) => `/journeys/${journeyToken}/applications/${journeyApplicationToken}/assign`,
      assignJourneyApplicationAgents: '/journeys/applications/assign',
      unassignJourneyApplicationAgents: ({ journeyApplicationToken, journeyToken }) => `/journeys/${journeyToken}/applications/${journeyApplicationToken}/unassign`,
      getApplications: '/journeys/applications',
      rerunApplication: ({ journeyApplicationToken, journeyToken }) => `/journeys/${journeyToken}/applications/${journeyApplicationToken}/rerun`,
      reviewJourneyApplication: ({ journeyApplicationToken, journeyToken }) => `/journeys/${journeyToken}/applications/${journeyApplicationToken}/review`,
      manualReviewEntityApplication: ({ journeyApplicationToken, journeyToken, caseToken }) => `/journeys/${journeyToken}/applications/${journeyApplicationToken}/cases/${caseToken}/review`,
      applicationQueueAgentQueries: '/journeys/applications/agentQuery',
      startApplicationQueueExport: format => `/journeys/applications/export/${format}`,
      nodes: {
        updateActionNode: (journeyToken, journeyApplicationToken, nodeId) => `/journeys/${journeyToken}/applications/${journeyApplicationToken}/nodes/${nodeId}`,
      },
      getApplicationActivity: ({ journeyToken, journeyApplicationToken }) => `/v1/journeys/${journeyToken}/applications/${journeyApplicationToken}/activity`,
      getApplicationStatusFilters:
        queryParameters => `/journeys/applications/status-filters${buildURLParamsString(queryParameters, true)}`,
      getEntityApplicationOutcomeFilters:
        queryParameters => `/journeys/applications/entity-application-event-outcomes-filters${buildURLParamsString(queryParameters, true)}`,
    },
    actions: {
      getActions: queryParameters => `/journeys/actions${buildURLParamsString(queryParameters, true)}`,
      getJourneysWithAction: journeyActionId => `/journeys/actions/${journeyActionId}/journeys-with-action`,
      archiveActionVersion: (actionId, version) => `/journeys/actions/${actionId}/${version}/archive`,
      createActionVersion: actionVersionId => `/journeys/actions/${actionVersionId}`,
      cloneActionVersion: actionVersionId => `/journeys/actions/${actionVersionId}/clone`,
    },
    championChallenger: journeyToken => `/journeys/${journeyToken}/champion-challenger`,
    championChallengers: '/journeys/champion-challengers',
    championChallengersHistory: journeyToken => `/journeys/${journeyToken}/champion-challengers-history`,
    childPayload: {
      getChildEntityPayloadsByEntityToken: ({ journeyToken, journeyApplicationToken }) => `/journeys/${journeyToken}/child-payloads/${journeyApplicationToken}`,
    },
    alerts: {
      getManualReview: ({
        alertManualReviewToken,
        journeyToken,
        journeyApplicationToken,
      }) => `/journeys/${journeyToken}/applications/${journeyApplicationToken}/review/${alertManualReviewToken}`,
    },
    notes: {
      getJourneyApplicationNotes: ({
        journeyToken,
        journeyApplicationToken,
      }) => `/journeys/${journeyToken}/applications/${journeyApplicationToken}/notes`,
    },
  },
  library: {
    local: {
      permittedSnippets: '/library/local/permitted-snippets',
    },
  },
  investigationAnalytics: {
    investigationsExist: '/investigation-analytics/investigations-exist',
    getAlertsOutcomes: '/investigation-analytics/alerts-outcomes',
    getAverageAge: '/investigation-analytics/average-age',
    getTotalEventsAlerts: '/investigation-analytics/total-events-alerts',
    getInvestigationsClosedOpen: '/investigation-analytics/investigations-closed-open',
    getInvestigationsOpenTotal: '/investigation-analytics/investigations-open-total',
    getAlertsOpenTotal: '/investigation-analytics/alerts-open-total',
    getInvestigationsOutcomes: '/investigation-analytics/investigations-outcomes',
    getInvestigationsStatus: '/investigation-analytics/investigations-status',
    getInvestigationsDailyVolume: '/investigation-analytics/investigations-daily-volume',
    getInvestigationsByAge: '/investigation-analytics/investigations-by-age',
    getInvestigationsOutcomesFlow: '/investigation-analytics/outcomes-flow',
    getAlertsDailyVolume: '/investigation-analytics/alerts-daily-volume',
    getAlertsPerformance: '/investigation-analytics/alerts-performance',
  },
  investigations: {
    createInvestigationType: '/investigations/types',
    getInvestigationTypeUseCases: '/investigations/use-cases',
    getInvestigationTypes: '/investigations/types',
    getInvestigationStateTypes: '/investigations/state-types',
    deletetInvestigationTypes: investigationType => `/investigations/types/delete/${investigationType}`,
    archiveInvestigationTypes: '/investigations/types/archive',
    unarchiveInvestigationTypes: '/investigations/types/unarchive',
    getInvestigationsByJAToken:
      queryParameters => `/investigations/investigations-by-ja${buildURLParamsString(queryParameters, true)}`,
    getInvestigationsByType:
      queryParameters => `/investigations/investigations-by-type${buildURLParamsString(queryParameters, true)}`,
    addAlertsToInvestigation: investigationToken => `/investigations/${investigationToken}/alerts`,
    createInvestigation: '/investigations/',
    assignableAgents: '/investigations/assignable-agents',
    alertAssignableAgents: '/investigations/alerts/assignable-agents',
    assignAgent: '/investigations/assign',
    unassign: '/investigations/unassign',
    addNoteToInvestigation: investigationToken => `/investigations/${investigationToken}/notes`,
    alerts: {
      assignAgent: ({ investigationToken, journeyApplicationToken }) => `/investigations/${investigationToken}/alerts/${journeyApplicationToken}/assign`,
      data: ({ investigationToken, journeyApplicationToken }) => `/investigations/${investigationToken}/alerts/${journeyApplicationToken}`,
      completeReview: ({ investigationToken, journeyApplicationToken }) => `/investigations/${investigationToken}/alerts/${journeyApplicationToken}/review`,
      submitManualReview: ({
        alertManualReviewToken,
        investigationToken,
        journeyApplicationToken,
      }) => `/investigations/${investigationToken}/alerts/${journeyApplicationToken}/review/${alertManualReviewToken}/review`,
      unassignAgent: ({ investigationToken, journeyApplicationToken }) => `/investigations/${investigationToken}/alerts/${journeyApplicationToken}/unassign`,
    },
    notes: {
      saveNotes: ({ investigationToken }) => `/investigations/${investigationToken}/notes`,
    },
    addEvidence: ({ investigationToken }) => `/investigations/${investigationToken}/evidences`,
    bulkAlertReview: ({ investigationToken }) => `/investigations/${investigationToken}/alerts/review`,
    getInvestigationSummary: ({ investigationToken }) => `/investigations/${investigationToken}/summary`,
    archiveInvestigations: '/investigations/archive',
    unarchiveInvestigations: '/investigations/unarchive',
    getInvestigationWebhookAuditEventDetails: ({
      investigationToken,
      webhookAuditEventToken,
    }) => `/investigations/${investigationToken}/webhook-events/${webhookAuditEventToken}`,
  },
  rateLimits: {
    get: '/rate-limits',
    patch: '/rate-limits',
  },
  apiKeys: {
    secrets: '/api-keys/secrets',
    create: '/api-keys',
    update: (token: string) => `/api-keys/${token}`,
    createSecret: (token: string) => `/api-keys/${token}`,
    deactivateSecret: (token: string, secretSerialId: string) => `/api-keys/${token}/${secretSerialId}/deactivate`,
    getSecrets: (token: string) => `/api-keys/${token}/secrets`,
  },
  apiKeyRoles: {
    list: '/api-key-roles',
    eligiblePermissions: '/api-key-roles/eligible-permissions',
    create: '/api-key-roles',
    update: (apiKeyRoleId: number) => `/api-key-roles/${apiKeyRoleId}`,
    archive: (apiKeyRoleId: number) => `/api-key-roles/${apiKeyRoleId}/archive`,
  },
  settings: {
    webhookRequests: {
      get: queryParameters => `/outgoing-webhooks/webhook-request${buildURLParamsString(queryParameters)}`,
    },
    webhookRequest: {
      get: id => `/outgoing-webhooks/webhook-request/${id}`,
    },
    webhookLogFilterOptions: {
      get: '/outgoing-webhooks/filter-options',
    },
    customerWebhook: {
      delete: webhookUuid => `/outgoing-webhooks/customer-webhook/${webhookUuid}`,
      getList: '/outgoing-webhooks/customer-webhooks',
      post: '/outgoing-webhooks/customer-webhook',
      put: webhookUuid => `/outgoing-webhooks/customer-webhook/${webhookUuid}`,
    },
    customerCertificates: {
      get: 'outgoing-webhooks/certificate',
    },
    sdk: {
      getList: '/sdk/client-internal/client',
      createKey: '/sdk/client-internal',
      archiveKey: '/sdk/client-internal/client',
      regenerateKey: identifier => `/sdk/client-internal/key/${identifier}`,
      logo: identifier => `/sdk/files/logo/${identifier}`,
      updateClient: identifier => `/sdk/client-internal/client/${identifier}`,
      allowlist: identifier => `/sdk/client-internal/allowlist/${identifier}`,
      clientServices: identifier => `/sdk/client-services/client-services/${identifier}`,
      journeyAssociatedKeys: journey_token => `/sdk/client-internal/key/journey/${journey_token}`,
      getJourneyKeyByToken: journey_token => `/sdk/client-internal/key/journeyToken/${journey_token}`,
      workflowAssociatedKeys: workflow_token => `/sdk/client-internal/key/workflow/${workflow_token}`,
      createHostedLink: '/sdk/temp-url',
    },
    events: {
      sourceWorkflows: '/events/source-workflows',
      archiveSourceWorkflow: '/events/source-workflows/archive',
      workflowEligibleEvents: '/events/workflow-eligible-events',
      eventStatuses: eventType => `/events/${eventType}/statuses`,
      events: '/events',
      bulkCreateMappings: '/events/source-workflows/bulk',
      patchEventStatus: (eventType, eventStatusToken) => `/events/${eventType}/statuses/${eventStatusToken}`,
      getEventLogs: queryParam => `/events/logs${queryParam}`,
      getBatchLogs: queryParam => `/events/ingestion-batches${queryParam}`,
    },
    services: '/services',
    serviceConfigOptions: '/service-config-options',
    /** @deprecated - remove when BUILD-4645 is complete */
    customerServiceConfigOptions: '/service-config-options/config-options',
    accountServiceConfigs: '/service-config-options/customer-services',
    serviceConfigOption: service_id => `/service-config-options/customer-service/${service_id}`,
    tags: '/tags',
    customerPartners: '/partners',
    reasonCodes: '/reason-codes',
    triggeredReasonCodes: '/triggered-reason-codes',
    fraudScores: '/service-fraud-scores',
    attributes: '/attributes',
    universalAttributes: '/services/universal-attributes',
    featureFlags: {
      getSegments: '/featureFlags/segments',
      getUserSegments: '/featureFlags/user/segments',
      updateUserSegments: (segment: string) => `/featureFlags/user/segments/${segment.toLowerCase()}`,
    },
    outcomes: {
      getOutcomes: options => `/outcomes${buildURLParamsString(options, true)}`,
      createOutcome: '/outcomes',
      updateOutcome: encodedOutcome => `/outcomes/${encodedOutcome}`,
      archiveOutcome: encodedOutcome => `/outcomes/${encodedOutcome}/archive`,
      unarchiveOutcome: encodedOutcome => `/outcomes/${encodedOutcome}/unarchive`,
      deleteOutcome: ({ encodedOutcome, customerOutcomeId }) => `/outcomes/${encodedOutcome}?customerOutcomeId=${customerOutcomeId}`,
    },
  },
  whatIf: {
    evaluations: analysisToken => `/what-if/${analysisToken}/evaluations`,
  },
  workflow: {
    getSchema: workflowToken => `/workflow/${workflowToken}/schema`,
    getVersionsDetails: '/workflow/versions',
    getVersions: workflowToken => `/workflow/${workflowToken}/versions`,
    getVersionMeta: (workflowToken, publicVersion) => `/workflow/${workflowToken}/version/${publicVersion}/meta`,
    archive: workflowToken => `/workflow/${workflowToken}/archive`,
    unarchive: workflowToken => `/workflow/${workflowToken}/unarchive`,
    histogram: {
      mla: '/workflow/attribute/histogram/mla',
    },
    schedules: {
      portfolio: workflowToken => `/portfolio-schedules/${workflowToken}`,
    },
    thresholdSimulator: {
      getBreakdown: ({
        publicId,
        workflowVersion,
        startDate,
        endDate,
      }) => {
        let breakdownUrl = `/workflow/threshold-simulator/breakdown/${publicId}?workflowVersion=${workflowVersion}`;
        if (startDate) {
          breakdownUrl = `${breakdownUrl}&startDate=${startDate}`;
        }
        if (endDate) {
          breakdownUrl = `${breakdownUrl}&endDate=${endDate}`;
        }
        return breakdownUrl;
      },
      getParams: ({
        publicId,
        workflowVersion,
      }) => `/workflow/threshold-simulator/params/${publicId}?workflowVersion=${workflowVersion}`,
    },
    skuCatalog: '/workflow-skus',
  },
  workflowCheckouts: {
    postCheckin: (workflowToken: string) => `/workflow-checkouts/${workflowToken}/checkin`,
    postCheckinOverride: (workflowToken: string) => `/workflow-checkouts/${workflowToken}/checkin-override`,
    workflowCheckout: (workflowToken: string) => `/workflow-checkouts/${workflowToken}/checkout`,
  },
  workflowValidation: {
    get: tagName => `/workflow-validation/tag/${tagName}`,
  },
  lists: {
    get: '/lists',
    post: '/lists',
    put: listToken => `/lists/${listToken}/meta`,
    listToken: {
      post: listToken => `/lists/${listToken}`,
      put: listToken => `/lists/${listToken}`,
    },
  },
  dataExports: {
    getDetails: exportToken => `/exports/${exportToken}`,
    download: exportToken => `/exports/${exportToken}/download`,
  },
  sandboxPersonas: {
    list: '/sandbox-personas',
    post: '/sandbox-personas',
    put: personaToken => `/sandbox-personas/${personaToken}`,
    archive: personaToken => `/sandbox-personas/${personaToken}`,
    getWorkflowSandboxPersonas: workflowToken => `/sandbox-personas/workflow/${workflowToken}`,
    checkSandboxPersonaNamesForConflicts: '/sandbox-personas/checkSandboxPersonaNamesForConflicts',
  },
  publishedAttributes: {
    list: '/published-attributes',
    get: attributeToken => `/published-attributes/${attributeToken}`,
    post: '/published-attributes',
    patch: attributeToken => `/published-attributes/${attributeToken}`,
    values: {
      get: attributeToken => `/published-attributes/${attributeToken}/values`,
      post: attributeToken => `/published-attributes/${attributeToken}/values`,
    },
    groupings: {
      list: '/published-attributes/groupings',
      post: '/published-attributes/groupings',
      patch: groupingToken => `/published-attributes/groupings/${groupingToken}`,
    },
  },
  portfolioEvaluations: {
    evaluations: '/portfolio-evaluations',
    filters: '/portfolio-evaluations/filters',
    evaluationDetails: ({
      portfolioEvaluationToken, page, paginateBy, q,
    }) => `/portfolio-evaluations/${portfolioEvaluationToken}?paginateBy=${paginateBy}&page=${page}&q=${q}`,
    entityDetail: ({
      dimensions,
      entityToken,
      portfolioEvaluationToken,
    }) => `/portfolio-evaluations/${portfolioEvaluationToken}/entity-detail/${entityToken}?${dimensions ? `dimensions=${dimensions}` : ''}`,
  },
  watchlists: {
    get: ({ canResolve, entityToken, evaluationToken }) => `/watchlists/entities/${entityToken}/evaluations/${evaluationToken}${canResolve ? '?can_resolve=true' : ''}`,
    post: ({ entityToken, evaluationToken }) => `/watchlists/matches/entities/${entityToken}/evaluations/${evaluationToken}`,
    postAIWatchlistResolver: ({ evaluationToken }) => `/ai/watchlist-resolver/resolve/${evaluationToken}`,
    postAIBatch: '/ai/batch',
    getBatchRunStatus: (runId: string) => `/ai/batch/${runId}`,
    getBatchFeatures: '/ai/batch/features',
  },
  ctr: {
    getAll: '/ctr/reports' as const,
    getOne: (reportToken: string) => `/ctr/reports/${reportToken}` as const,
    getAssignableAgents: '/ctr/agents/assignable' as const,
    assign: (reportToken: string) => `/ctr/reports/${reportToken}/assign` as const,
    create: '/ctr/reports' as const,
    update: (reportToken: string) => `/ctr/reports/${reportToken}` as const,
    submit: (reportToken: string) => `/ctr/reports/${reportToken}/submit` as const,
  },
  customerGroups: {
    list: '/child-customer-groups',
    create: '/child-customer-groups',
    edit: (token: string) => `/child-customer-groups/${token}`,
  },
  partnerSolutions: {
    services: {
      customerServices: '/services/customer-services',
      solutionInterestTypes: '/services/solution-interest-types',
      solutionLearnMoreRequest: '/services/solution-learn-more-request',
      detail: serviceInternalName => `/services/${serviceInternalName}`,
      attributes: {
        list: serviceInternalName => `/services/${serviceInternalName}/attributes`,
        detail: (serviceInternalName, attributeId) => `/services/${serviceInternalName}/attributes/${attributeId}`,
        matchingMetadata: '/services/attributes/matching-metadata',
      },
      changeLogs: serviceInternalName => `/services/${serviceInternalName}/change-logs`,
      sampleResponse: serviceInternalName => `/services/${serviceInternalName}/sample-response`,
    },
    partners: {
      getPartner: partnerName => `/partners/${partnerName}`,
    },
    channelPartners: {
      getAllChannelPartners: '/channel-partners',
      customerChannelPartners: '/channel-partners/customer',
      customerChannelPartnersById: customerId => `/channel-partners/customer/${customerId}`,
    },
  },

  policyTests: {
    getPrerequisites: '/policy-tests/prerequisites',
    getTestResults: (testToken: string) => `/policy-tests/executions/${testToken}`,
    getTestTypes: '/policy-tests/test-types',
    createConfig: '/policy-tests/configurations',
    runTest: '/policy-tests/executions',
    dataSample: '/policy-tests/configurations/data-sample',
    dataSampleFilters: '/policy-tests/configurations/data-sample/filters',
  },

  customLists: {
    get: '/custom-lists',
    post: '/custom-lists',
    customListToken: {
      patchMeta: customListToken => `/custom-lists/${customListToken}`,
      getVersions: customListToken => `/custom-lists/${customListToken}`,
      getVersion: (customListToken, version) => `/custom-lists/${customListToken}/versions/${version}`,
      postVersion: customListToken => `/custom-lists/${customListToken}/versions`,
      append: customListToken => `/custom-lists/${customListToken}/append`,
      activateVersion: (customListToken, version) => `/custom-lists/${customListToken}/activate/${version}`,
    },
  },

  customModels: {
    get: '/custom-models',
  },

  clientQueryDataConfig: {
    getObjects: '/client-query-data-config/objects',
  },

  services: {
    attributes: {
      jqValidation: '/services/attributes/jq-validation',
    },
  },

  dataSourcesModal: {
    getVendorApiCallsAndServiceResponses: (evaluationToken, queryParams) => `/vendor-api-calls/${evaluationToken}${buildURLParamsString(queryParams)}`,
  },

  logDecryption: {
    get: ({
      service, year, month, day, hour, minute, second, logType, encryptedLogId,
    }) => {
      if (second) {
        return `/log-decryption/${service}/${year}/${month}/${day}/${hour}/${minute}/${second}/${logType}/${encryptedLogId}`;
      }
      if (minute) {
        return `/log-decryption/${service}/${year}/${month}/${day}/${hour}/${minute}/${logType}/${encryptedLogId}`;
      }
      return `/log-decryption/${service}/${year}/${month}/${day}/${hour}/${logType}/${encryptedLogId}`;
    },
  },
};

const externalRoutes = {
  helpArticles: {
    whatIf: 'https://help.alloy.com/hc/en-us/articles/4406813719195-Using-What-If-Analysis',
  },
};

const getRoute = (selector, routeParams) => {
  const route = get(routes, selector);
  if (typeof route === 'function') return route(routeParams);
  return route;
};

export {
  routes,
  typhonRoutes,
  externalRoutes,
  getRoute,
  wrapVanityDomainOnRoute,
};
